

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export default function DeviceCombobox({ devices, value, onChange, placeholder = "Search device ID or MAC…", disabled = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);
  const [rect, setRect]   = useState(null);
  const boxRef      = useRef(null);
  const inputRef    = useRef(null);
  const dropdownRef = useRef(null);

  const updateRect = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      const maxPanelHeight = 220;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const openUp = spaceBelow < maxPanelHeight + 8 && spaceAbove > spaceBelow;
      const panelHeight = Math.min(maxPanelHeight, (openUp ? spaceAbove : spaceBelow) - 8);
      setRect({
        left: r.left,
        width: r.width,
        maxHeight: Math.max(120, panelHeight),
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    }
  };

  useEffect(() => {
    const onClick = (e) => {
      const insideBox = boxRef.current && boxRef.current.contains(e.target);
      const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!insideBox && !insideDropdown) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    updateRect();
    const onMove = () => updateRect();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 120);
  };

  const selected = devices.find((d) => d.id === value);
  const q = query.trim().toLowerCase();
  const filtered = !q
    ? devices
    : devices.filter((d) =>
        d.id?.toLowerCase().includes(q) || d.mac_address?.toLowerCase().includes(q)
      );

  const pick = (id) => {
    onChange(id);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        value={open ? query : (selected ? `${selected.id} · ${selected.mac_address || "—"}` : "")}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: "100%", boxSizing: "border-box",
          background: disabled ? "var(--surface-alt)" : "var(--surface)", border: `1px solid var(--border)`,
          borderRadius: 6, padding: "7px 10px", fontSize: 12,
          color: "var(--text)", fontFamily: "'IBM Plex Mono', monospace", outline: "none",
          cursor: disabled ? "not-allowed" : "text", opacity: disabled ? 0.7 : 1,
        }}
      />
      {open && !disabled && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed", left: rect.left, width: rect.width,
            top: rect.top, bottom: rect.bottom,
            background: "var(--surface)", border: `1px solid var(--border)`,
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            maxHeight: rect.maxHeight, overflowY: "auto", zIndex: 1000,
          }}
        >
          <div
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick("")}
            style={{
              padding: "8px 12px", cursor: "pointer", fontSize: 12,
              color: "var(--text-dim)", borderBottom: `1px solid var(--border)`,
              background: !value ? "var(--surface-alt)" : "transparent",
            }}
          >
            — Enter new device —
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-dim)" }}>
              No matching devices.
            </div>
          ) : filtered.map((d) => (
            <div
              key={d.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(d.id)}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: 12,
                fontFamily: "'IBM Plex Mono', monospace",
                color: "var(--ink)",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                background: value === d.id ? "var(--surface-alt)" : "transparent",
              }}
            >
              {d.id} · {d.mac_address || "—"}
              <span style={{ color: "var(--text-dim)", fontFamily: "'DM Sans', system-ui, sans-serif", marginLeft: 6 }}>
                {d.affiliate_id ? `(${d.affiliate_id})` : "(unassigned)"}
              </span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}